/**
 * EID-126: pure classifier for transform spec-entry changes.
 *
 * The data-provider's transform watcher diffs per-entry JSON snapshots
 * (snapshot-diff.ts, EID-61) and used to stop+start the watcher — terminating
 * the Web Worker and re-importing all CDN modules — on ANY change. This
 * classifier tells the fast path apart: a change confined to `dataSpec.code`
 * can be recompiled in the live (warm) worker via Transform.setCode, while
 * input/module/outputType (or any other) changes keep the full-teardown path.
 *
 * Classification is conservative: anything that cannot be positively
 * identified as a code-only change (new entry, unparsable snapshot, missing
 * code, differing key order) falls back to `restart`, which is always
 * correct — just slower.
 *
 * Kept dependency-free (no 'framework'/'utils' barrel imports) so it can be
 * unit tested per the repo testing note.
 */

export type TransformSpecChange =
  /** Only dataSpec.code changed — recompile the live worker with `code`. */
  | { kind: 'code-only'; code: string }
  /** Anything else changed (or the entry is new) — full stop+start. */
  | { kind: 'restart' }
  /** Snapshots are identical — nothing to do. */
  | { kind: 'unchanged' };

/** Re-serialise an entry with its code masked, preserving key order. */
const withoutCode = (entry: { dataSpec?: Record<string, unknown> }): string =>
  JSON.stringify({
    ...entry,
    dataSpec: { ...entry.dataSpec, code: null },
  });

/**
 * Classify the change between the previously applied snapshot of a transform
 * spec entry and its next snapshot (both JSON.stringify of the whole `data`
 * entry, as produced by the data-provider's snapshot memos).
 */
export function classifyTransformSpecChange(
  prevSnapshot: string | undefined,
  nextSnapshot: string,
): TransformSpecChange {
  if (prevSnapshot === undefined) return { kind: 'restart' };
  if (prevSnapshot === nextSnapshot) return { kind: 'unchanged' };
  let prev: { dataSpec?: Record<string, unknown> };
  let next: { dataSpec?: Record<string, unknown> };
  try {
    prev = JSON.parse(prevSnapshot);
    next = JSON.parse(nextSnapshot);
  } catch {
    return { kind: 'restart' };
  }
  const prevCode = prev?.dataSpec?.code;
  const nextCode = next?.dataSpec?.code;
  if (typeof prevCode !== 'string' || typeof nextCode !== 'string')
    return { kind: 'restart' };
  if (prevCode === nextCode) return { kind: 'restart' };
  // Code differs; it is a code-only change iff everything else is untouched.
  // Key-order differences (e.g. a remote patch rewriting the entry) fail this
  // comparison and take the restart path — conservative by design.
  if (withoutCode(prev) !== withoutCode(next)) return { kind: 'restart' };
  return { kind: 'code-only', code: nextCode };
}
